import { useMemo, useCallback } from 'react';
import { FEATURES, FeatureId, FeatureKeys } from 'features/definitions';
import useBrand from 'Hooks/useBrand';
import useUser from 'Hooks/useUser';
import useOrganization from 'Hooks/useOrganization';
import { PlanType } from 'modules/Const';

export interface UseFeatureResult {
  /** Whether the feature is available for the current user */
  isAvailable: boolean;
  /** Whether the user needs to upgrade to access this feature */
  requiresUpgrade: boolean;
  /** Get the limit for this feature (if applicable) */
  getLimit: () => number | string | null;
  /** Get an upgrade message for this feature */
  getUpgradeMessage: () => string;
  /** The minimum plan required for this feature */
  minRequiredPlan: PlanType | null;
  /** All plans that support this feature */
  availablePlans: readonly PlanType[];
}

/**
 * Hook to check feature availability based on the current user's plan
 * @param featureId - The feature ID to check
 * @returns Object with feature availability information
 */
export const useFeature = (featureId: FeatureId): UseFeatureResult => {
  const { currentBrand, isGrandfathered, loadedBrands } = useBrand();
  const { isSymphonyAdmin } = useUser();
  const { organization } = useOrganization();
  
  const feature = FEATURES[featureId];
  
  const isAvailable = useMemo(() => {
    if (!currentBrand?.tier) return false;
    
    const userPlan = currentBrand.tier;
    
    // Special logic for TEAM_MULTI_BRAND feature
    if (featureId === FeatureKeys.TEAM_MULTI_BRAND) {
      // Always allow admins to add unlimited brands
      if (isSymphonyAdmin) {
        return true;
      }
      
      // Check if user has more than the limit (3 brands)
      const currentBrandCount = loadedBrands?.length || 0;
      const limit = ('limits' in feature && feature.limits && typeof feature.limits === 'object') 
        ? (feature.limits as Record<string, number | string>)[userPlan] 
        : null;
      
      // If they haven't reached the limit yet, allow it
      if (limit && typeof limit === 'number' && currentBrandCount < limit) {
        return true;
      }
      
      // If they have Team plan AND have an organization, allow unlimited brands
      if (userPlan === PlanType.TEAM && organization) {
        return true;
      }
      
      // For other cases, check if they have Team_V2 plan
      if (userPlan === PlanType.TEAM_V2) {
        return true;
      }
      
      // Otherwise, they need to upgrade
      return false;
    }
    
    // Check grandfathered status first - grandfathered users get special access
    if (isGrandfathered && 'grandfatheredPlans' in feature && feature.grandfatheredPlans) {
      const grandfatheredPlans = feature.grandfatheredPlans as readonly PlanType[];
      if (grandfatheredPlans.includes(userPlan)) {
        return true;
      }
    }
    
    // Check regular plans
    const plans = feature.plans as readonly PlanType[];
    return plans.includes(userPlan);
  }, [currentBrand?.tier, isGrandfathered, isSymphonyAdmin, organization, loadedBrands, featureId, feature]);

  const getLimit = useCallback(() => {
    if (!currentBrand?.tier) return null;
    
    // Type guard to check if feature has limits
    if ('limits' in feature && feature.limits) {
      const userPlan = currentBrand.tier;
      const limits = feature.limits as Record<string, number | string>;
      return limits[userPlan] ?? null;
    }
    
    return null;
  }, [currentBrand?.tier, feature]);

  const requiresUpgrade = !isAvailable;
  
  const minRequiredPlan = useMemo(() => {
    // Find the "lowest" plan that supports this feature
    const planHierarchy = [
      PlanType.FREE,
      PlanType.STARTER, 
      PlanType.LITE,
      PlanType.PRO,
      PlanType.GROWTH,
      PlanType.TEAM,
      PlanType.TEAM_V2
    ];
    
    const plans = feature.plans as readonly PlanType[];
    for (const plan of planHierarchy) {
      if (plans.includes(plan)) {
        return plan;
      }
    }
    
    return null;
  }, [feature]);
  
  const getUpgradeMessage = useCallback(() => {
    if (!minRequiredPlan) {
      return `Upgrade to unlock ${feature.name}`;
    }
    
    // Convert plan type to user-friendly name
    const planNames: Record<PlanType, string> = {
      [PlanType.FREE]: 'Free',
      [PlanType.STARTER]: 'Starter',
      [PlanType.LITE]: 'Lite', 
      [PlanType.PRO]: 'Pro',
      [PlanType.GROWTH]: 'Growth',
      [PlanType.TEAM]: 'Team',
      [PlanType.TEAM_V2]: 'Team'
    };
    
    const planName = planNames[minRequiredPlan] || minRequiredPlan;
    return `Upgrade to ${planName} to unlock ${feature.name}`;
  }, [feature, minRequiredPlan]);
  
  return {
    isAvailable,
    requiresUpgrade,
    getLimit,
    getUpgradeMessage,
    minRequiredPlan,
    availablePlans: feature.plans
  };
};

/**
 * Hook to get multiple features at once
 * @param featureIds - Array of feature IDs to check
 * @returns Object with each feature's availability information
 */
export const useFeatures = (featureIds: FeatureId[]) => {
  const { currentBrand, isGrandfathered } = useBrand();
  
  return useMemo(() => {
    const results: Record<string, UseFeatureResult> = {};
    
    featureIds.forEach(featureId => {
      const feature = FEATURES[featureId];
      const userPlan = currentBrand?.tier;
      
      let isAvailable = false;
      if (currentBrand && userPlan) {
        // Check grandfathered status first
        if (isGrandfathered && 'grandfatheredPlans' in feature && feature.grandfatheredPlans) {
          const grandfatheredPlans = feature.grandfatheredPlans as readonly PlanType[];
          if (grandfatheredPlans.includes(userPlan)) {
            isAvailable = true;
          }
        }
        
        if (!isAvailable) {
          // Check regular plans
          const plans = feature.plans as readonly PlanType[];
          isAvailable = plans.includes(userPlan);
        }
      }
      
      results[featureId] = {
        isAvailable,
        requiresUpgrade: !isAvailable,
        getLimit: () => {
          if (!currentBrand?.tier) return null;
          if ('limits' in feature && feature.limits) {
            const limits = feature.limits as Record<string, number | string>;
            return limits[currentBrand.tier] ?? null;
          }
          return null;
        },
        getUpgradeMessage: () => `Upgrade to unlock ${feature.name}`,
        minRequiredPlan: (feature.plans as readonly PlanType[])[0] || null,
        availablePlans: feature.plans
      };
    });
    
    return results;
  }, [featureIds, currentBrand?.tier, isGrandfathered]);
}; 